import React, { useState } from "react";
import { Gift } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { apiClient } from "@/lib/api";
import { capture } from "@/lib/analytics";
import { toast } from "sonner";

/**
 * Compact "Have a referral code?" affordance.
 * - Logged out: stores code in localStorage and shows confirmation
 * - Logged in: claims via POST /api/referral/claim immediately
 */
export default function ReferralCodeInput({ centered = true, className = "" }) {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  if (user?.has_referral_discount) return null;

  const submit = async () => {
    const c = code.trim().toUpperCase();
    if (!c) return;
    if (!user) {
      try {
        localStorage.setItem("pending_referral_code", c);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[referral] could not stash code in localStorage:", e?.message);
      }
      capture("referral_code_stashed", { code_prefix: c.slice(0, 4) });
      toast.success("Code saved — sign up and we'll apply your discount.");
      setOpen(false);
      setCode("");
      return;
    }
    if (user.referral_first_used) {
      toast.error("You've already used your first-time referral discount.");
      return;
    }
    setBusy(true);
    try {
      await apiClient.post("/referral/claim", { code: c });
      await refresh?.();
      capture("referral_signup", { code_prefix: c.slice(0, 4) });
      capture("referral_code_applied", { code_prefix: c.slice(0, 4) }); // legacy event for existing dashboards
      toast.success("Referral applied — 90% off Monthly or 30% off Annual at checkout.");
      setOpen(false);
      setCode("");
    } catch (e) {
      const msg = e?.response?.data?.detail || "That code isn't valid.";
      capture("referral_code_failed", { code_prefix: c.slice(0, 4), reason: msg });
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`max-w-md ${centered ? "mx-auto" : ""} ${className}`} data-testid="referral-code-input-container">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="inline-flex items-center justify-center min-h-[44px] gap-1.5 text-sm font-semibold text-[var(--primary)] hover:underline px-2 py-2"
          data-testid="referral-code-toggle"
        >
          <Gift size={14} /> Have a referral code?
        </button>
      ) : (
        <div className="card-base p-4 text-left">
          <div className="flex items-center gap-2 mb-2">
            <Gift size={14} className="text-[var(--secondary)]" />
            <span className="text-xs font-semibold uppercase tracking-wider text-[var(--ink-2)]">Apply a referral code</span>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              className="brut-input flex-1 font-mono uppercase text-sm"
              placeholder="FRIEND-XXXX"
              autoFocus
              spellCheck={false}
              data-testid="referral-code-input"
            />
            <button
              onClick={submit}
              disabled={busy || !code.trim()}
              className="btn-duo whitespace-nowrap inline-flex items-center min-h-[44px]"
              style={{ padding: "0.6rem 1.1rem", fontSize: "0.85rem" }}
              data-testid="referral-code-apply"
            >
              {busy ? <span className="spinner" /> : "Apply"}
            </button>
          </div>
          <p className="text-xs text-[var(--ink-3)] mt-2">
            {user
              ? "Unlocks 90% off AI Mastery Monthly or 30% off Annual on your first checkout. Annual offer expires 30 days from claim."
              : "We'll save your code and apply it after sign-up — 90% off Monthly or 30% off Annual."}
          </p>
        </div>
      )}
    </div>
  );
}
